import { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare, Plus, RefreshCw, ChevronRight,
  AlertCircle, User, Phone, Mail, Building, Clock,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { createPqrsf, fetchPqrsf, updatePqrsfEstado } from '@/services/pqrsf.service';

const TIPOS   = ['Petición', 'Queja', 'Reclamo', 'Sugerencia', 'Felicitación'];
const CANALES = ['Presencial', 'Telefónico', 'Correo electrónico', 'WhatsApp', 'Portal web', 'Escrito'];
const ESTADOS = ['Abierto', 'En proceso', 'Cerrado', 'Archivado'];

const TIPO_COLOR = {
  'Petición':     'bg-indigo-100 text-indigo-700',
  'Queja':        'bg-red-100 text-red-700',
  'Reclamo':      'bg-orange-100 text-orange-700',
  'Sugerencia':   'bg-teal-100 text-teal-700',
  'Felicitación': 'bg-pink-100 text-pink-700',
};
const ESTADO_COLOR = {
  'Abierto':    'bg-blue-100 text-blue-700',
  'En proceso': 'bg-amber-100 text-amber-700',
  'Cerrado':    'bg-green-100 text-green-700',
  'Archivado':  'bg-gray-100 text-gray-500',
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const now16 = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);

const EMPTY_FORM = {
  tipo: '',
  canal: '',
  fecha_recepcion: now16(),
  descripcion: '',
  observaciones_internas: '',
  solicitante: { nombre: '', identificacion: '', organizacion: '', cargo: '', correo: '', telefono: '' },
};

// ─── Field helpers ────────────────────────────────────────────
const FieldLabel = ({ children, required }) => (
  <label className="text-xs font-medium text-gray-600 block mb-1">
    {children}{required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const SectionTitle = ({ children }) => (
  <p className="text-xs font-bold uppercase text-gray-500 tracking-wide border-b pb-1 mb-3">{children}</p>
);

const NativeSelect = ({ value, onChange, children, className = '' }) => (
  <select
    value={value}
    onChange={onChange}
    className={`w-full text-sm border border-input rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring ${className}`}
  >
    {children}
  </select>
);

// ─── PqrsfPanel ───────────────────────────────────────────────
const PqrsfPanel = ({ clienteId, clienteNombre, clienteNit, idLavanderia }) => {
  const { toast } = useToast();
  const [records, setRecords]       = useState([]);
  const [isLoading, setIsLoading]   = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [isSaving, setIsSaving]     = useState(false);
  const [formData, setFormData]     = useState(EMPTY_FORM);
  const [filterTipo, setFilterTipo]     = useState('');
  const [filterEstado, setFilterEstado] = useState('');

  const loadData = useCallback(async () => {
    if (!clienteId) return;
    setIsLoading(true);
    try {
      const query = { 'cliente.id': clienteId };
      if (filterTipo)   query.tipo   = filterTipo;
      if (filterEstado) query.estado = filterEstado;
      const res = await fetchPqrsf({ query, limit: 100 });
      setRecords(res.data || []);
    } catch {
      toast({ title: 'Error', description: 'No se pudieron cargar los registros PQRSF', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [clienteId, filterTipo, filterEstado]);

  useEffect(() => { loadData(); }, [loadData]);

  const setField = (path, value) =>
    setFormData(prev => {
      if (path.startsWith('solicitante.')) {
        const key = path.split('.')[1];
        return { ...prev, solicitante: { ...prev.solicitante, [key]: value } };
      }
      return { ...prev, [path]: value };
    });

  const openCreate = () => {
    setFormData({ ...EMPTY_FORM, fecha_recepcion: now16() });
    setShowCreate(true);
  };

  const handleCreate = async () => {
    const { tipo, canal, descripcion, solicitante } = formData;
    if (!tipo || !canal || !descripcion.trim() || !solicitante.nombre.trim() || !solicitante.identificacion.trim()) {
      toast({ title: 'Campos requeridos', description: 'Tipo, canal, descripción, nombre e identificación son obligatorios', variant: 'destructive' });
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        cliente: { id: clienteId, nombre: clienteNombre || '', nit: clienteNit || '' },
        lavanderia: idLavanderia ? { id: idLavanderia, nombre: '' } : undefined,
      };
      const created = await createPqrsf(payload);
      toast({ title: 'PQRSF registrado', description: `Consecutivo asignado: ${created.consecutivo}` });
      setShowCreate(false);
      loadData();
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo crear el registro', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateEstado = async (id, estado) => {
    try {
      const updated = await updatePqrsfEstado(id, estado);
      setRecords(prev => prev.map(r => r._id === id ? updated : r));
      if (showDetail?._id === id) setShowDetail(updated);
      toast({ title: 'Estado actualizado' });
    } catch {
      toast({ title: 'Error', description: 'No se pudo actualizar el estado', variant: 'destructive' });
    }
  };

  // Stats
  const abiertos = records.filter(r => r.estado === 'Abierto' || r.estado === 'En proceso').length;
  const statsByTipo = TIPOS.reduce((a, t) => ({ ...a, [t]: records.filter(r => r.tipo === t).length }), {});

  return (
    <>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-amber-600" />
              PQRSF — FT-CMC-01
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Peticiones, Quejas, Reclamos, Sugerencias y Felicitaciones</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadData} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" onClick={openCreate} className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5">
              <Plus className="h-4 w-4" />
              Nuevo PQRSF
            </Button>
          </div>
        </div>

        {/* Stats por tipo */}
        {records.length > 0 && (
          <div className="grid grid-cols-5 gap-2">
            {TIPOS.map(t => (
              <div key={t} className={`rounded-lg px-2 py-2 text-center ${TIPO_COLOR[t]}`}>
                <p className="text-xl font-bold">{statsByTipo[t]}</p>
                <p className="text-[11px] font-medium leading-tight mt-0.5">{t}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <NativeSelect value={filterTipo} onChange={e => setFilterTipo(e.target.value)} className="w-auto text-xs">
            <option value="">Todos los tipos</option>
            {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
          </NativeSelect>
          <NativeSelect value={filterEstado} onChange={e => setFilterEstado(e.target.value)} className="w-auto text-xs">
            <option value="">Todos los estados</option>
            {ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
          </NativeSelect>
          {abiertos > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
              <AlertCircle className="h-3.5 w-3.5" />
              {abiertos} pendiente{abiertos !== 1 ? 's' : ''} de gestión
            </span>
          )}
        </div>

        {/* List */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="text-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto" />
                <p className="text-sm text-gray-400 mt-2">Cargando registros...</p>
              </div>
            ) : records.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-500 font-medium">Sin registros PQRSF</p>
                <p className="text-xs text-gray-400 mt-1">Usa "Nuevo PQRSF" para crear el primer registro</p>
              </div>
            ) : (
              <div className="divide-y">
                {records.map(r => (
                  <button
                    key={r._id}
                    onClick={() => setShowDetail(r)}
                    className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-bold text-gray-600">{r.consecutivo}</span>
                        <Badge className={`text-xs border-0 ${TIPO_COLOR[r.tipo]}`}>{r.tipo}</Badge>
                        <Badge className={`text-xs border-0 ${ESTADO_COLOR[r.estado]}`}>{r.estado}</Badge>
                      </div>
                      <p className="text-sm text-gray-700 mt-1 font-medium">{r.solicitante?.nombre}</p>
                      <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{r.descripcion}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDate(r.fecha_recepcion)}</span>
                        <span>{r.canal}</span>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-300 shrink-0 mt-1" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Create Dialog ── */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-amber-600" />
              Nuevo Registro PQRSF — FT-CMC-01
            </DialogTitle>
            <DialogDescription>
              Complete los datos del solicitante y la descripción del caso.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-1">
            {/* Tipificación */}
            <section>
              <SectionTitle>Tipificación</SectionTitle>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel required>Tipo</FieldLabel>
                  <NativeSelect value={formData.tipo} onChange={e => setField('tipo', e.target.value)}>
                    <option value="">Seleccionar...</option>
                    {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                  </NativeSelect>
                </div>
                <div>
                  <FieldLabel required>Canal de recepción</FieldLabel>
                  <NativeSelect value={formData.canal} onChange={e => setField('canal', e.target.value)}>
                    <option value="">Seleccionar...</option>
                    {CANALES.map(c => <option key={c} value={c}>{c}</option>)}
                  </NativeSelect>
                </div>
              </div>
              <div className="mt-3">
                <FieldLabel required>Fecha y hora de recepción</FieldLabel>
                <input
                  type="datetime-local"
                  value={formData.fecha_recepcion}
                  onChange={e => setField('fecha_recepcion', e.target.value)}
                  className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </section>

            {/* Solicitante */}
            <section>
              <SectionTitle>Datos del solicitante</SectionTitle>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel required>Nombre completo</FieldLabel>
                  <Input value={formData.solicitante.nombre} onChange={e => setField('solicitante.nombre', e.target.value)} placeholder="Nombre completo" className="text-sm" />
                </div>
                <div>
                  <FieldLabel required>Identificación</FieldLabel>
                  <Input value={formData.solicitante.identificacion} onChange={e => setField('solicitante.identificacion', e.target.value)} placeholder="CC / NIT" className="text-sm" />
                </div>
                <div>
                  <FieldLabel>Organización</FieldLabel>
                  <Input value={formData.solicitante.organizacion} onChange={e => setField('solicitante.organizacion', e.target.value)} placeholder="Empresa u organización" className="text-sm" />
                </div>
                <div>
                  <FieldLabel>Cargo</FieldLabel>
                  <Input value={formData.solicitante.cargo} onChange={e => setField('solicitante.cargo', e.target.value)} placeholder="Cargo" className="text-sm" />
                </div>
                <div>
                  <FieldLabel>Correo electrónico</FieldLabel>
                  <Input type="email" value={formData.solicitante.correo} onChange={e => setField('solicitante.correo', e.target.value)} placeholder="correo@ejemplo.com" className="text-sm" />
                </div>
                <div>
                  <FieldLabel>Teléfono</FieldLabel>
                  <Input value={formData.solicitante.telefono} onChange={e => setField('solicitante.telefono', e.target.value)} placeholder="Teléfono de contacto" className="text-sm" />
                </div>
              </div>
            </section>

            {/* Descripción */}
            <section>
              <SectionTitle>Descripción del caso</SectionTitle>
              <FieldLabel required>Descripción detallada (en palabras del solicitante)</FieldLabel>
              <textarea
                value={formData.descripcion}
                onChange={e => setField('descripcion', e.target.value)}
                placeholder="Descripción detallada del caso..."
                rows={4}
                className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </section>

            {/* Observaciones internas */}
            <section>
              <SectionTitle>Uso interno</SectionTitle>
              <FieldLabel>Observaciones internas</FieldLabel>
              <textarea
                value={formData.observaciones_internas}
                onChange={e => setField('observaciones_internas', e.target.value)}
                placeholder="Notas internas para el equipo (no visibles al solicitante)..."
                rows={2}
                className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </section>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} disabled={isSaving}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={isSaving} className="bg-amber-600 hover:bg-amber-700 text-white">
              {isSaving ? 'Registrando...' : 'Registrar PQRSF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detail Dialog ── */}
      {showDetail && (
        <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-mono text-base">
                {showDetail.consecutivo}
                <Badge className={`text-xs border-0 ${TIPO_COLOR[showDetail.tipo]}`}>{showDetail.tipo}</Badge>
              </DialogTitle>
              <DialogDescription>{fmtDate(showDetail.fecha_recepcion)} · {showDetail.canal}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-1 text-sm">
              {/* Estado */}
              <div>
                <p className="text-xs font-bold uppercase text-gray-500 mb-2">Estado del caso</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {ESTADOS.map(e => (
                    <button
                      key={e}
                      onClick={() => handleUpdateEstado(showDetail._id, e)}
                      className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all border ${
                        showDetail.estado === e
                          ? ESTADO_COLOR[e] + ' border-current ring-2 ring-offset-1 ring-current'
                          : 'border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>

              {/* Solicitante */}
              <div className="border-t pt-3">
                <p className="text-xs font-bold uppercase text-gray-500 mb-2">Solicitante</p>
                <div className="space-y-1.5">
                  <p className="font-semibold text-gray-800 flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    {showDetail.solicitante?.nombre}
                    <span className="text-xs text-gray-400 font-normal">({showDetail.solicitante?.identificacion})</span>
                  </p>
                  {showDetail.solicitante?.organizacion && (
                    <p className="text-sm text-gray-600 flex items-center gap-2">
                      <Building className="h-4 w-4 text-gray-400" />
                      {showDetail.solicitante.organizacion}
                      {showDetail.solicitante.cargo && <span className="text-gray-400">· {showDetail.solicitante.cargo}</span>}
                    </p>
                  )}
                  <div className="flex gap-4 flex-wrap">
                    {showDetail.solicitante?.correo && (
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Mail className="h-3.5 w-3.5" />{showDetail.solicitante.correo}
                      </p>
                    )}
                    {showDetail.solicitante?.telefono && (
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Phone className="h-3.5 w-3.5" />{showDetail.solicitante.telefono}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Descripción */}
              <div className="border-t pt-3">
                <p className="text-xs font-bold uppercase text-gray-500 mb-2">Descripción del caso</p>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm">{showDetail.descripcion}</p>
              </div>

              {/* Observaciones internas */}
              {showDetail.observaciones_internas && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
                  <p className="text-xs font-bold text-yellow-700 mb-1">Observaciones internas</p>
                  <p className="text-xs text-yellow-800">{showDetail.observaciones_internas}</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetail(null)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default PqrsfPanel;
